<template>
  <main class="container">
    <table class="table table-success table-striped">
      <thead>
      <tr>
        <th>OPTIONS</th>
        <th>LINK</th>
        <th>SHORT LINK</th>
        <th>QR CODE</th>
      </tr>
      </thead>
      <tbody>
      <!-- Show only paginated links -->
      <tr v-for="dataLink in paginatedLinks" :key="dataLink.id_links">
        <td class="d-flex flex-column flex-lg-row justify-content-evenly align-items-center flex-strow">
          <button class="btn btn-primary option-button" @click="editLink(dataLink.id_links, dataLink.original_link)">
            Change link
          </button>
          <button class="btn btn-danger option-button" @click="confirmAndDeleteLink(dataLink.id_links)">
            Delete link
          </button>
        </td>
        <td>
          <span
              class="align-middle"
              tabindex="0"
              role="button"
              :title="dataLink.original_link"
              @click="showFullUrl(dataLink.original_link)"
              style="cursor: pointer"
          >
            {{ truncateUrl(dataLink.original_link) }}
          </span>
        </td>
        <td class="align-middle">
          <span
              class="align-middle"
              tabindex="0"
              role="button"
              :title="formatShortLink(dataLink.short_link)"
              @click="showFullUrl(formatShortLink(dataLink.short_link))"
              style="cursor: pointer"
          >
            {{ formatShortLink(dataLink.short_link) }}
          </span>
        </td>
        <td class="align-middle">
          <img v-if="dataLink.qrCode" :src="dataLink.qrCode" alt="QR Code" @click="showQRCodeModal(dataLink)"/>
        </td>
      </tr>
      </tbody>
    </table>

    <!-- Bootstrap 5 pagination controls -->
    <nav v-if="totalPages > 1" aria-label="Page navigation">
      <ul class="pagination justify-content-center">
        <li class="page-item" :class="{ disabled: currentPage === 1 }">
          <button class="page-link" @click="goToPage(currentPage - 1)" :disabled="currentPage === 1">Previous</button>
        </li>
        <li
            v-for="page in totalPages"
            :key="page"
            class="page-item"
            :class="{ active: currentPage === page }"
        >
          <button class="page-link" @click="goToPage(page)">{{ page }}</button>
        </li>
        <li class="page-item" :class="{ disabled: currentPage === totalPages }">
          <button class="page-link" @click="goToPage(currentPage + 1)" :disabled="currentPage === totalPages">Next
          </button>
        </li>
      </ul>
    </nav>
  </main>
</template>

<script>
import axios from "axios";
import QRCode from "qrcode";

import {
  confirmAndDeleteLinkService,
  editLink,
  formatShortLinkService,
  showQRCodeModal,
  truncateUrl
} from "@/services/links/linksComponentService";

const apiUrl = process.env.VUE_APP_API_URL;

export default {
  name: "LinksTable",
  /**
   * Provides the initial state of the data object.
   *
   * @return {Object} An object containing the `dataLinks` array initialized as an empty array.
   */
  data() {
    return {
      dataLinks: [],
      currentPage: 1, // Current page number
      pageSize:5,   // Items per page
    };
  },
  /**
   * A set of computed properties for handling pagination logic.
   * These properties calculate the paginated links and the total number of pages
   * based on the current state of the data.
   *
   * @property {Function} paginatedLinks - Computes and returns a subset of links
   *                                       for the current page based on
   *                                       currentPage and pageSize.
   * @property {Function} totalPages - Computes and returns the total number of
   *                                   pages based on the number of links available
   *                                   and the page size.
   */
  computed: {
    // Returns the links for the current page
    /**
     * Retrieves a subset of links from the dataLinks array corresponding to the current page.
     *
     * This method calculates the starting index based on the current page and page size,
     * and slices the dataLinks array to include only the items for the specific page.
     *
     * @return {Array} A subset of links for the current page.
     */
    paginatedLinks() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.dataLinks.slice(start, start + this.pageSize);
    },
    // Calculates the total number of pages
    /**
     * Calculates the total number of pages based on the total number of items
     * and the number of items per page.
     *
     * @return {number} The total number of pages.
     */
    totalPages() {
      return Math.ceil(this.dataLinks.length / this.pageSize);
    }
  },
  /**
   * Lifecycle method invoked after the component is mounted. Retrieves user-specific link data from the server
   * using the stored session ID and token. Converts each link into a QR code format and initializes popovers for the component.
   * If session ID or token is not available, logs an error message to the console.
   *
   * @return {Promise<void>} A promise that resolves when the method execution is complete, including API calls
   * and QR code generation, or logs an error if the session data is missing or requests fail.
   */
  async mounted() {
    const id = sessionStorage.getItem('id');
    const token = sessionStorage.getItem('token');

    if (id && token) {
      try {
        const response = await axios.get(`${apiUrl}/links/id_user/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        const data = response.data;
        const linksWithQR = await Promise.all(
            data.links.map(async link => ({
              ...link,
              qrCode: await QRCode.toDataURL(formatShortLinkService(link.short_link))
            }))
        );
        this.dataLinks = linksWithQR;

      } catch (error) {
        console.error('Error getting data:', error);
      }
    } else {
      console.error('Id or token was not found in sessionStorage');
    }
  },
  /**
   * Contains methods for managing and interacting with links, such as formatting, editing,
   * deleting, and displaying QR codes or full URLs.
   */
  methods: {
    /**
     * A service function that formats a given link into a shortened version.
     * This typically involves removing unnecessary portions of a URL or creating
     * a condensed representation of the original link to enhance readability and usability.
     *
     * The specific implementation of the formatting logic is provided by the `formatShortLinkService` function.
     *
     * @function formatShortLink
     * @type {Function}
     */
    formatShortLink: formatShortLinkService,
    /**
     * Shortens a given URL to a specified maximum length.
     * If the URL exceeds the maximum length, it adds an ellipsis ("...")
     * to indicate truncation while preserving its essential structure.
     *
     * @function
     * @param {string} url - The URL to be truncated.
     * @param {number} maxLength - The maximum length of the truncated URL.
     * @returns {string} The truncated URL with an ellipsis indicating truncation
     *                   if it exceeds the specified maximum length. If the URL
     *                   length is within the limit, it returns the URL unchanged.
     */
    truncateUrl,
    /**
     * Edits the link's URL.
     *
     * @param {Object} link - The link object that needs to be edited.
     * @param {string} url - The new URL to assign to the link.
     * @return {void}
     */
    editLink(link, url) {
      editLink(this, link, url);
    },
    /**
     * Confirms and deletes a link associated with the given identifier.
     *
     * @param {string} id - The unique identifier of the link to be confirmed and deleted.
     * @return {Promise<void>} A promise that resolves when the link is successfully confirmed and deleted.
     */
    async confirmAndDeleteLink(id) {
      await confirmAndDeleteLinkService(this, id);
    },
    /**
     * Displays a modal containing a QR code generated from the provided data link.
     *
     * @param {string} dataLink - The data used to generate the QR code displayed in the modal.
     * @return {Promise<void>} A promise that resolves when the QR code modal has been successfully displayed.
     */
    async showQRCodeModal(dataLink) {
      await showQRCodeModal(this, dataLink);
    },
    /**
     * Displays a modal with the given URL, providing an option to copy it to the clipboard.
     *
     * @param {string} url - The URL to be displayed in the modal.
     * @return {Promise<void>} Resolves when the URL is successfully copied to clipboard, or rejects if an error occurs.
     */
    showFullUrl(url) {
      this.$swal.fire({
        title: "URL",
        html: `<a href="${url}" target="_blank" rel="noopener">${url}</a>`,
        confirmButtonText: "Copy and close",
        preConfirm: () => {
          return navigator.clipboard.writeText(url);
        }
      });
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
  }
};
</script>


